// docs/142 DY6 — the dyer's counter: choose one of your heroes, see them as they are and in each of the thirteen
// choices (their clothes as drawn, then the twelve dyes), which of the pieces they wear take the dye and which keep
// their own colour, and pay the dyer to dye them (`redye_hero`, 40 gold; the module judges). docs/156: or dye one
// piece, worn or in the pack (`dye_item`, 10 gold), which keeps that colour on whoever wears it.
import { useMemo, useState } from 'react';
import { Button, DyeSwatches, HeroFigure, Panel, Segmented, Select } from '../../components';
import { ItemArtIcon } from '../../inventory/ItemCard';
import { DYE_CHOICES, DYE_PRICE, OUTFIT_DYE_PRICE, dyedPieces, dyeName, dyeSummary, dyeTake } from '../../../core/heroes/dyes';
import { bareLook, type HeroLook, type Slot } from '../../../game/heroKit';
import { artDefOf } from '../../../game/baseItemArt';
import { SHOP_TITLES, SHOP_TRADES } from '../../../core/items/upgrades';
import type { WorkshopView } from './Workshop';

/** A piece a hero has: its item id, catalogue def, name and the dye it is in now (0: as drawn). */
export interface DyerPiece { id: bigint; def: number; name: string; dye: number }

export interface DyerHero {
  id: bigint; name: string; gold: number;
  /** How they look now (their dye and any piece dyed on its own included). */
  look?: HeroLook;
  /** What they wear. */
  worn: ReadonlyArray<DyerPiece>;
  /** docs/156: what is in their pack (only pieces that take a dye are offered). */
  pack?: ReadonlyArray<DyerPiece>;
}

type Mode = 'outfit' | 'piece';

/** The look with every piece in `dye` (no piece dyed on its own). */
const allIn = (look: HeroLook, dye: number): HeroLook => ({ species: look.species, sex: look.sex, outfit: look.outfit, dye });
/** The slot a worn piece is drawn in, if it is drawn. */
const slotOf = (look: HeroLook, def: number): Slot | undefined => {
  const art = artDefOf(def);
  return (Object.keys(look.outfit) as Slot[]).find((s) => look.outfit[s] === art);
};

export function Dyer({ workshop, heroes, busy, onDye, onDyePiece, onClose }: {
  workshop: WorkshopView; heroes: DyerHero[]; busy?: string;
  onDye(heroId: bigint, dye: number, payerId: bigint, label: string): void;
  onDyePiece(itemId: bigint, dye: number, payerId: bigint, label: string): void;
  onClose(): void;
}) {
  const [heroId, setHeroId] = useState<bigint | undefined>(heroes[0]?.id);
  const hero = heroes.find((h) => h.id === heroId) ?? heroes[0];
  const look = hero?.look ?? bareLook();
  const [mode, setMode] = useState<Mode>('outfit');
  const choosable = useMemo(() => [
    ...(hero?.worn ?? []).filter((p) => dyeTake(p.def) === 'dyes').map((p) => ({ ...p, worn: true })),
    ...(hero?.pack ?? []).filter((p) => dyeTake(p.def) === 'dyes').map((p) => ({ ...p, worn: false })),
  ], [hero]);
  const [pieceId, setPieceId] = useState<bigint>();
  const piece = mode === 'piece' ? choosable.find((p) => p.id === pieceId) ?? choosable[0] : undefined;
  const pieceSlot = piece?.worn ? slotOf(look, piece.def) : undefined;
  // the whole outfit is one colour when no piece is dyed on its own
  const uniform = !look.dyes || Object.keys(look.dyes).length === 0;
  const now = piece ? piece.dye : look.dye;
  const [chosen, setChosen] = useState<number>();
  const dye = chosen ?? (now === 0 ? 1 : 0);
  const [payerId, setPayerId] = useState<bigint>();
  const payer = heroes.find((h) => h.id === payerId) ?? hero;
  const pieces = useMemo(() => dyedPieces(hero?.worn ?? []), [hero]);
  const takes = pieces.some((p) => p.takes);
  const price = piece ? DYE_PRICE : OUTFIT_DYE_PRICE;
  const short = !!payer && payer.gold < price;
  const already = piece ? dye === now : dye === now && uniform;
  const pick = (id: bigint) => { setHeroId(id); setChosen(undefined); setPayerId(undefined); setPieceId(undefined); };
  const choose = (m: Mode) => { setMode(m); setChosen(undefined); };
  /** The hero (or, for a piece in the pack, the piece) in dye `d`. */
  const shownIn = (d: number): HeroLook => (!piece ? allIn(look, d) : pieceSlot ? { ...look, dyes: { ...look.dyes, [pieceSlot]: d } } : look);
  const figure = (d: number, scale: 2 | 3, title: string, breathe: boolean) => (piece && !pieceSlot
    ? <ItemArtIcon def={piece.def} name={piece.name} scale={scale === 3 ? 4 : 2} dye={d} title={title} />
    : <HeroFigure look={shownIn(d)} scale={scale} {...(breathe ? { breathe: true } : { frame: 0 })} title={title} />);
  const who = piece ? `the ${piece.name}` : hero?.name ?? '';
  const title = SHOP_TITLES.dyer;
  return (
    <Panel variant="solid" className="fk-talk__counter fk-counter fk-ws fk-dyer fk-hit" label={`${title}: dyes`} data-testid="dyer" data-shop="dyer">
      <header className="fk-talk__counter-head">
        <span>{title}</span>
        <Button small variant="ghost" onClick={onClose} aria-label="Close the dyer's counter" data-testid="dyer-close">✕</Button>
      </header>
      <p className="fk-ws__trade">{workshop.keeper ? <><b>{workshop.keeper}</b> — </> : null}{SHOP_TRADES.dyer}</p>
      {!hero ? <p className="fk-faint fk-counter__empty">None of your heroes is here to be dyed.</p> : (
        <>
          {heroes.length > 1 && (
            <Segmented label="Whose clothes" className="fk-counter__tabs" value={hero.id.toString()} onChange={(v) => pick(BigInt(v))}
              options={heroes.map((h) => ({ value: h.id.toString(), label: h.name }))} />
          )}
          <div data-testid="dyer-mode" data-mode={mode}>
            <Segmented<Mode> label="What to dye" className="fk-counter__tabs" value={mode} onChange={choose}
              options={[{ value: 'outfit', label: `Whole outfit · ${OUTFIT_DYE_PRICE}g` }, { value: 'piece', label: `One piece · ${DYE_PRICE}g` }]} />
          </div>
          {mode === 'piece' && (choosable.length === 0
            ? <p className="fk-faint fk-counter__empty" data-testid="dyer-no-pieces">Nothing {hero.name} has takes a dye.</p>
            : (
              <ul className="fk-dyer__picks" aria-label="Which piece" data-testid="dyer-piece-list">
                {choosable.map((p) => (
                  <li key={p.id.toString()}>
                    <button type="button" className={`fk-dyer__pick${p.id === piece?.id ? ' is-on' : ''}`} aria-pressed={p.id === piece?.id}
                      onClick={() => { setPieceId(p.id); setChosen(undefined); }} title={`${p.name}${p.worn ? '' : ' (in the pack)'} — ${dyeName(p.dye)}`}
                      data-testid="dyer-piece" data-item-id={p.id.toString()} data-dye={p.dye} data-worn={p.worn}>
                      <ItemArtIcon def={p.def} name={p.name} scale={2} dye={p.dye} />
                      <small>{p.name}{p.worn ? '' : ' · pack'}</small>
                    </button>
                  </li>
                ))}
              </ul>
            ))}
          {(mode === 'outfit' || piece) && (
            <>
              <div className="fk-dyer__now" data-testid="dyer-now" data-dye={uniform || piece ? now : 'mixed'}>
                <figure>
                  <span className="fk-dyer__stage">{figure(now, 3, `${who} as ${piece ? 'it is' : 'they are'}`, true)}</span>
                  <figcaption>As {piece ? 'it is' : 'they are'}<br /><small>{piece || uniform ? dyeName(now) : 'Mixed'}</small></figcaption>
                </figure>
                <span className="fk-dyer__arrow" aria-hidden>→</span>
                <figure>
                  <span className="fk-dyer__stage fk-dyer__stage--to">{figure(dye, 3, `${who} in ${dyeName(dye)}`, true)}</span>
                  <figcaption>Dyed<br /><small>{dyeName(dye)}</small></figcaption>
                </figure>
              </div>
              <ul className="fk-dyer__grid" aria-label={piece ? 'Every dye on it' : 'Every dye on them'} data-testid="dyer-grid">
                {DYE_CHOICES.map((d) => (
                  <li key={d}>
                    <button type="button" className={`fk-dyer__choice${d === dye ? ' is-on' : ''}${d === now && (piece || uniform) ? ' is-now' : ''}`} onClick={() => setChosen(d)} aria-pressed={d === dye}
                      title={`${dyeName(d)}${d === now && (piece || uniform) ? piece ? ' (as it is)' : ' (as they are)' : ''}`} data-testid="dyer-choice" data-dye={d}>
                      {figure(d, 2, dyeName(d), false)}
                      <small>{dyeName(d)}</small>
                    </button>
                  </li>
                ))}
              </ul>
              <DyeSwatches value={dye} current={now} onChange={setChosen} label="Dye" data-testid="dyer-swatches" />
            </>
          )}
          {mode === 'outfit' && (
            <>
              <ul className="fk-dyer__pieces" aria-label="What takes the dye" data-testid="dyer-pieces">
                {pieces.map((p) => (
                  <li key={`${p.def}-${p.name}`} className={p.takes ? 'is-dyed' : 'is-kept'} data-def={p.def} data-takes={p.takes}>
                    <span aria-hidden>{p.takes ? '◆' : '◇'}</span> {p.name} <span className="fk-faint">{p.takes ? `takes the dye` : p.slot === 'main_hand' ? '— a weapon keeps its colour' : '— keeps its own colour'}</span>
                  </li>
                ))}
              </ul>
              <p className="fk-faint fk-dyer__summary">{dyeSummary(pieces)}</p>
            </>
          )}
          {mode === 'piece' && piece && (
            <p className="fk-faint fk-dyer__summary" data-testid="dyer-piece-note">
              {piece.worn ? `Only the ${piece.name} is dyed; the rest of ${hero.name}'s clothes stay as they are.` : `The ${piece.name} is dyed in the pack.`} It keeps the colour on whoever wears it.
            </p>
          )}
          <div className="fk-ws__pay">
            <span className="fk-counter__details-foot">
              {heroes.length > 1 ? (
                <label className="fk-dyer__payer">Paid by{' '}
                  <Select value={payer?.id.toString() ?? ''} onChange={(e) => setPayerId(BigInt(e.target.value))} data-testid="dyer-payer">
                    {heroes.map((h) => <option key={h.id.toString()} value={h.id.toString()}>{h.name} · {h.gold}g</option>)}
                  </Select>
                </label>
              ) : <>{payer?.name} pays ({payer?.gold} in purse).</>}
            </span>
            {piece ? (
              <Button small primary disabled={!!busy || already || short || !payer} loading={busy === `dye-item-${piece.id}-${dye}`}
                title={already ? 'It is that colour already' : short ? `${payer?.name} has ${payer?.gold} gold` : undefined}
                onClick={() => payer && onDyePiece(piece.id, dye, payer.id, `${hero.name}'s ${piece.name} is dyed ${dyeName(dye).toLowerCase()}`)} data-testid="dyer-buy">
                {dye === 0 ? 'Back to natural' : `Dye it ${dyeName(dye).toLowerCase()}`} · {DYE_PRICE}g
              </Button>
            ) : mode === 'outfit' ? (
              <Button small primary disabled={!!busy || already || short || !takes || !payer} loading={busy === `dye-${hero.id}-${dye}`}
                title={already ? 'Their clothes are that colour already' : !takes ? 'Nothing they wear takes a dye' : short ? `${payer?.name} has ${payer?.gold} gold` : undefined}
                onClick={() => payer && onDye(hero.id, dye, payer.id, `${hero.name}'s clothes are dyed ${dyeName(dye).toLowerCase()}`)} data-testid="dyer-buy">
                {dye === 0 ? 'Back to natural' : `Dye ${dyeName(dye).toLowerCase()}`} · {OUTFIT_DYE_PRICE}g
              </Button>
            ) : null}
          </div>
        </>
      )}
    </Panel>
  );
}
